import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  ImageResizeMode,
  ImageSourcePropType,
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from "react-native";
import { colors, motion } from "../theme/tokens";

type Props = {
  source?: ImageSourcePropType;
  fallbackLabel?: string;
  resizeMode?: ImageResizeMode;
  reduceMotion?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  onReady?: () => void;
  onError?: () => void;
};

export function MediaImage({
  source,
  fallbackLabel,
  resizeMode = "cover",
  reduceMotion = false,
  style,
  testID,
  onReady,
  onError,
}: Props) {
  const sourceKey = useMemo(() => JSON.stringify(source ?? null), [source]);
  const [failed, setFailed] = useState(!source);
  const reveal = useRef(new Animated.Value(source ? 0 : 1)).current;

  useEffect(() => {
    setFailed(!source);
    reveal.stopAnimation();
    reveal.setValue(source ? 0 : 1);
  }, [reveal, sourceKey]);

  const handleReady = () => {
    Animated.timing(reveal, {
      toValue: 1,
      duration: reduceMotion ? 0 : motion.imageReveal,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
    onReady?.();
  };

  const handleError = () => {
    setFailed(true);
    reveal.setValue(1);
    onError?.();
  };

  return (
    <View testID={testID} style={[styles.root, style]}>
      <View style={styles.fallback}>
        {fallbackLabel ? (
          <Text style={styles.fallbackText}>
            {fallbackLabel.slice(0, 1).toUpperCase()}
          </Text>
        ) : null}
      </View>
      {source && !failed ? (
        <Animated.Image
          source={source}
          // Ask Fresco to downsample before retaining the bitmap. This is
          // important on Android TV where a decoded source can otherwise keep
          // its server dimensions despite the card's smaller layout size.
          resizeMethod={Platform.OS === "android" ? "resize" : undefined}
          resizeMode={resizeMode}
          onLoad={handleReady}
          onError={handleError}
          style={[StyleSheet.absoluteFillObject, { opacity: reveal }]}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    overflow: "hidden",
    backgroundColor: colors.surfaceRaised,
  },
  fallback: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.surfaceRaised,
  },
  fallbackText: {
    color: "rgba(255,255,255,0.28)",
    fontSize: 20,
    fontWeight: "900",
  },
});
